import { useEffect, useRef } from "react";

import { Route, Routes, useNavigate } from "react-router-dom";

import supabase, { authCallbackParams } from "./utils/Supabase";
import { getUserInfo, blacklistUser, updateUserStrikes } from "./utils/UserUtil";

import useUserStore from "./stores/User";

import Home from "./pages/Home";
import AccountPage from "./pages/AccountPage";
import AboutPage from "./pages/AboutPage";
import LegalPage from "./pages/LegalPage";
import DatabasePage from "./pages/DatabasePage";
import ScriptView from "./pages/ScriptView";
import ScriptEdit from "./pages/ScriptEdit";
import RequestsPage from "./pages/RequestsPage";
import CuratorsPage from "./pages/CuratorsPage";
import DatabaseLogin from "./pages/DatabaseLogin";
import Register from "./pages/Register";
import ResendRegistration from "./pages/ResendRegistration";
import CheckRights from "./pages/CheckRights";
import AdminPage from "./pages/AdminPage";
import IndexerDashboard from "./pages/IndexerDashboard";
import ResetPasword from "./pages/ResetPassword";
import NewPassword from "./pages/NewPassword";
import BaseLayout from "./pages/BaseLayout";
import PlatinumDatabasePage from "./pages/PlatinumDatabasePage";
import ScriptRoulette from "./pages/ScriptRoulette";
import FeaturedPage from "./pages/FeaturedPage";
import FeaturedScriptView from "./pages/FeaturedScriptView";
import NotFoundPage from "./pages/404page";

import Footer from "./components/Footer";
import AuthGuard from "./components/AuthGuard";
import ErrorBoundary from "./components/ErrorBoundary";

import { ToastContainer } from "react-toastify";
import { showToast } from "./utils/Toasts";

import "react-toastify/dist/ReactToastify.css";

import "rc-slider/assets/index.css";

import "./sass/main.sass";

function App() {
  const userData = useUserStore((state) => state.data);
  const setUserData = useUserStore((state) => state.setUserData);
  const setLoading = useUserStore((state) => state.setLoading);
  const navigate = useNavigate();
  const automationProcessedUserRef = useRef<string | null>(null);

  const checkSession = async () => {
    const session = await supabase.auth.getUser();
    if (!session || !session.data.user) {
      setLoading(false);
      return;
    }
  }

  const searchUserData = async (userId: string) => {
    const data = await getUserInfo(userId);
    setUserData(data);
  };

  useEffect(() => {
    // Supabase doesn't tell us which flow an expired link belonged to, so go by
    // where it landed. Recovery links redirect to /newPassword and that page
    // reports the error itself, so leave those alone.
    if (
      authCallbackParams.errorCode === "otp_expired" &&
      window.location.pathname.toLowerCase() !== "/newpassword"
    ) {
      navigate({ pathname: "/resendRegistration" });
    }

    if (window.location.href.indexOf("token_type=bearer&type=signup") > 0) {
      // Give Supabase time to process the auth tokens before checking rights
      setTimeout(() => {
        navigate({ pathname: "/checkRights" });
      }, 500);
    }
  }, [navigate]);

  useEffect(() => {
    if (userData) {
      if (!userData.user_id || automationProcessedUserRef.current === userData.user_id) {
        return;
      }

      const rawUserAgent = navigator.userAgent;
      const userAgent = rawUserAgent.toLowerCase();

      // List of common automation user agents to check against
      const automationAgents = [
        "curl/",
        "wget/",
        "postmanruntime",
        "python-requests",
        "python-urllib",
        "go-http-client",
        "headlesschrome",
        "puppeteer",
        "playwright",
        "selenium",
        "webdriver",
        "phantomjs",
        "chromedriver",
        "geckodriver",
        "scrapy",
      ];

      const isAutomation = automationAgents.some((agent) => userAgent.includes(agent));

      if (isAutomation && !userData?.isBlacklisted) {
        automationProcessedUserRef.current = userData.user_id;

        const doBlacklist = async () => {
          await blacklistUser(userData?.user_id);
          setUserData({ ...userData, isBlacklisted: true });
          await updateUserStrikes(userData?.user_id, userData?.email, "Automation Detected: User agent is " + rawUserAgent, "Ban");
          showToast('error', 'Your account is currently limited due to suspected automation. Confirmed scraping will result in a permanent ban.', 10000);
        }

        // Likely automation, blacklist the user
        doBlacklist();
      }
    }
  }, [userData]);
  
  useEffect(() => {
    checkSession();
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Check if a session can be initialized based on what is in localStorage
      if (event === "INITIAL_SESSION" && session && session?.user) {
        searchUserData(session.user.id);
      }

      if (event === "SIGNED_IN") {
        searchUserData(session.user.id);
      }

      if (event !== "SIGNED_OUT") {
        return;
      }

      setUserData(null);
      automationProcessedUserRef.current = null;
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Home />} />

        <Route element={<BaseLayout />}>
          <Route path="/databaseLogin" element={<DatabaseLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/resendRegistration" element={<ResendRegistration />} />
          <Route path="/checkRights" element={<CheckRights />} />
          <Route path="/resetPassword" element={<ResetPasword />} />
          <Route path="/newPassword" element={<NewPassword />} />
          <Route path="/requests" element={<RequestsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/featured" element={<FeaturedPage />} />
          <Route path="/featured/:slug" element={<FeaturedScriptView />} />
          <Route path="/*" element={<NotFoundPage />} />

          <Route element={<AuthGuard />}>
            <Route path="/account" element={<AccountPage />}/>
            <Route path="/admin">
              <Route index element={<AdminPage />} />
            </Route>
            <Route path="/curators" element={<CuratorsPage />} />
            <Route path="/indexerDashboard" element={<IndexerDashboard />} />
            <Route path="/database">
              <Route index element={<DatabasePage />} />
              <Route path="script/:file_id" element={<ScriptView />} />
              <Route path="script/:file_id/edit" element={<ScriptEdit />} />
            </Route>
            <Route path="/platinumDatabase" element={<PlatinumDatabasePage />} />
            <Route path="/roulette" element={<ScriptRoulette />} />
          </Route>
        </Route>
      </Routes>

      <Footer />

      <ToastContainer
        position="top-left"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
      />
    </ErrorBoundary>
  );
}

export default App;
